import React, { useCallback } from "react";
import { BrowserRouter, useNavigate } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";
import AppRoutes from "./routes/AppRoutes";
import FormsRoutes from "./routes/FormsRoutes";
import DesktopOnly from "./components/layout/DesktopOnly";
import { ToastProvider } from "./components/ui";
import { CurrentUserProvider } from "./features/auth/hooks/useCurrentUser";
import { ROUTES } from "./routes/paths";
import { API_ENVIRONMENT } from "./lib/api";
import { CLERK_PUBLISHABLE_KEY } from "./lib/clerk";
import { isFormsHost } from "./lib/site/config";

/** Shown instead of the app when the environment being built has no key,
 *  because Clerk otherwise fails with a stack trace that says nothing about
 *  the missing key. */
const MissingClerkKey = () => (
  <div className="min-h-screen flex items-center justify-center bg-white px-6">
    <div className="max-w-[480px] text-center">
      <h1 className="text-[20px] font-medium text-[#1E1F24]">
        Clerk is not configured
      </h1>
      <p className="mt-2 text-[14px] leading-6 text-[#62636C]">
        This build is aimed at <code>{API_ENVIRONMENT}</code>, and no
        publishable key is set for it. Fill in the {API_ENVIRONMENT} entry of{" "}
        <code>src/lib/clerk/publishableKeys.json</code> from Clerk Dashboard
        &rarr; API Keys and rebuild, or set{" "}
        <code>REACT_APP_CLERK_PUBLISHABLE_KEY</code> in <code>.env</code> to
        override it for a local run.
      </p>
    </div>
  </div>
);

/**
 * Clerk, wired to the router it is sitting inside.
 *
 * Clerk navigates on its own account - after a sign-out, when a handshake
 * cannot finish, whenever it reaches one of the URLs below. Given no router it
 * falls back to `window.location` for every one of those, which reloads the
 * whole app: the screen the user was sent to appears, then blanks and appears
 * again a moment later. Handed the router's own `navigate`, they become
 * ordinary in-app transitions.
 *
 * Hence this lives under `<BrowserRouter>` rather than above it, which is the
 * only reason it is a component of its own.
 */
export const ClerkWithRouter: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();

  // Clerk hands these a path, but an absolute URL would be pushed by the
  // router as a relative one, so it is unpacked either way. Off-origin is the
  // one case the router cannot take - and the one case Clerk does not use it
  // for, so this only ever runs if that changes.
  const go = useCallback(
    (to: string, replace: boolean) => {
      // Clerk moving the user is invisible from the screens' side - it carries
      // no router state and passes through no guard of ours - so say so.
      // Development only; see `routeTrace`.
      if (process.env.NODE_ENV !== "production") {
        console.warn(`[route] Clerk asked for ${to} (replace: ${replace})`);
      }

      let url: URL;
      try {
        url = new URL(to, window.location.origin);
      } catch {
        navigate(to, { replace });
        return;
      }

      if (url.origin !== window.location.origin) {
        window.location.href = to;
        return;
      }
      navigate(`${url.pathname}${url.search}${url.hash}`, { replace });
    },
    [navigate]
  );

  return (
    // The URLs point at our own screens so nothing Clerk does internally - a
    // redirect after sign-out, a guard sending an anonymous user away - can
    // ever land on Clerk's hosted Account Portal. The screens themselves use
    // the headless hooks, so no Clerk UI is rendered either.
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      routerPush={(to) => go(to, false)}
      routerReplace={(to) => go(to, true)}
      signInUrl={ROUTES.login}
      signUpUrl={ROUTES.signup}
      // Both land on the free trial, which is where this app's own auth screens
      // send people too — see `SsoCallbackPage` and `VerifyOtpPage`. That screen
      // is the one that asks whether there is a trial to be had and forwards to
      // the dashboard when there is not, so which path completed the session
      // makes no difference to where its reader ends up.
      signInFallbackRedirectUrl={ROUTES.trial}
      signUpFallbackRedirectUrl={ROUTES.trial}
      afterSignOutUrl={ROUTES.login}
    >
      {children}
    </ClerkProvider>
  );
};

function App() {
  // The forms a practitioner shares are served from their own host, and this
  // one bundle answers on both — so which product this is gets decided from
  // the address bar rather than at build time. See `lib/site/config`.
  //
  // Above everything below it, and deliberately: the person opening a form is
  // the practitioner's client, not a Naurist user. Mounting Clerk for them
  // would start a session handshake that can only fail, and the missing-key
  // notice below would replace somebody's enquiry form with a message about
  // our own configuration. They get a router and nothing else.
  if (isFormsHost()) {
    return (
      <BrowserRouter>
        <FormsRoutes />
      </BrowserRouter>
    );
  }

  if (!CLERK_PUBLISHABLE_KEY) {
    return <MissingClerkKey />;
  }

  return (
    // Outermost, so a viewport too narrow for the app never even starts Clerk
    // or the router — the notice is the whole page until the screen is wide
    // enough, and the app mounts fresh the moment it is.
    <DesktopOnly>
      <BrowserRouter>
        <ClerkWithRouter>
          {/* Above the route table for the same reason the toasts are: who is
              signed in does not change when the screen does, and one read of
              `/api/v1/me/` serves every screen that names them. Inside Clerk,
              because it has nothing to ask until there is a session. */}
          <CurrentUserProvider>
            {/* Outside the route table, so a message raised just before a
                navigation is not torn down by the screen change that follows
                it. BrowserRouter above never unmounts on a navigation, only the
                matched route below does. */}
            <ToastProvider>
              <AppRoutes />
            </ToastProvider>
          </CurrentUserProvider>
        </ClerkWithRouter>
      </BrowserRouter>
    </DesktopOnly>
  );
}

export default App;
